// src/hooks/useOperacaoLote.ts
import { useCallback, useRef, useState } from 'react';
import type { AxiosResponse } from 'axios';
import { useOperacoesLote } from '@/contexts/OperacaoLoteContext';
import type { OperacaoLoteResponse } from '@/types/lote/OperacaoLote';

/**
 * Peça por-tela do "selecionar tudo": dispara a ação e roteia a resposta dual do
 * backend (issue #56 / ADR-037).
 *
 *   - 200 / 201 → conjunto abaixo do limiar; corpo idêntico ao de hoje, tratado
 *                 AQUI, pela tela, porque o formato é específico dela;
 *   - 202       → conjunto acima do limiar; entregue ao OperacaoLoteProvider,
 *                 que acompanha até o fim e avisa o usuário onde quer que ele
 *                 esteja.
 *
 * O 202 só existe quando o request envia `selectAll: true` — garantido por teste
 * no backend desde a #65.
 *
 * POR QUE NÃO USA `useApiCall`: aquele hook faz `return response.data` e descarta
 * o status HTTP (useApiCall.ts:50), então é cego para a diferença entre 200 e
 * 202. Alterá-lo afetaria todas as chamadas do app (premissa #9), então o caminho
 * select-all recebe `apiPost`/`apiPut` prontos do chamador e lê a resposta inteira.
 */

export type ResultadoDisparo =
  /** Processado na hora; `onInline` já recebeu o corpo. */
  | 'inline'
  /** Aceito para segundo plano; o provider avisa quando terminar. */
  | 'aceito'
  /** Barrado pela trava local — já há operação em voo para este pai. */
  | 'bloqueado';

export interface DisparoLote<T = unknown> {
  /**
   * Identidade do pai que a ação trava, no formato `<ACAO>:<pai>`. Espelha o par
   * (acao, chave_pai) do backend — use `chaveLote()` do provider para montar.
   */
  chaveTrava: string;
  /** Executa a request. Deve devolver a resposta completa (apiPost / apiPut). */
  executar: () => Promise<AxiosResponse>;
  /** Corpo do caminho inline (200/201). Não é chamado no caminho assíncrono. */
  onInline?: (data: T, status: number) => void;
}

export const useOperacaoLote = () => {
  const { acompanhar, estaEmAndamento } = useOperacoesLote();

  // Janela entre o clique e a resposta. A trava do provider só começa quando o
  // 202 chega; sem esta, um duplo clique rápido dispararia duas requests antes
  // disso. Em ref para ser lida de forma SÍNCRONA no handler — estado do React
  // só chega no próximo render, tarde demais para barrar o segundo clique.
  const emVooRef = useRef<Set<string>>(new Set());
  // Contador só para re-renderizar quem observa `estaProcessando` (o ref sozinho
  // não avisa o React).
  const [, forcarRender] = useState(0);

  const marcarEmVoo = useCallback((chave: string, ligado: boolean) => {
    if (ligado) emVooRef.current.add(chave);
    else emVooRef.current.delete(chave);
    forcarRender((n) => n + 1);
  }, []);

  /**
   * Erros da request são repropagados para o chamador tratar: o backend pode
   * responder 400 por trava anti-reentrada, por permissão (a #69 antecipou essa
   * checagem para antes da resolução do conjunto) ou por filtro incoerente com a
   * tela.
   */
  const disparar = useCallback(
    async <T,>({ chaveTrava, executar, onInline }: DisparoLote<T>): Promise<ResultadoDisparo> => {
      if (emVooRef.current.has(chaveTrava) || estaEmAndamento(chaveTrava)) return 'bloqueado';
      marcarEmVoo(chaveTrava, true);

      try {
        const res = await executar();

        if (res.status === 202) {
          acompanhar(chaveTrava, res.data as OperacaoLoteResponse);
          return 'aceito';
        }

        onInline?.(res.data as T, res.status);
        return 'inline';
      } finally {
        // Solta a janela de voo nos dois caminhos: no assíncrono quem segura a
        // partir daqui é a trava do provider, que dura até o estado terminal.
        marcarEmVoo(chaveTrava, false);
      }
    },
    [estaEmAndamento, acompanhar, marcarEmVoo],
  );

  /** true da hora do clique até o fim do processamento — desabilita os botões. */
  const estaProcessando = useCallback(
    (chaveTrava: string) => emVooRef.current.has(chaveTrava) || estaEmAndamento(chaveTrava),
    [estaEmAndamento],
  );

  return { disparar, estaProcessando };
};
